import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Heart, Send } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";

export const Route = createFileRoute("/harikaradmin/likes")({
  head: () => ({ meta: [{ title: "Beğeniler — JUËN Admin" }] }),
  component: AdminLikesPage,
});

interface LikeRow {
  id: string;
  event_id: string;
  user_id: string;
  created_at: string;
  kind: "like" | "share";
}
interface Profile { id: string; first_name: string; last_name: string; email: string }
interface EventRow { id: string; name: string; starts_at: string }

function AdminLikesPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<LikeRow[]>([]);
  const [profiles, setProfiles] = useState<Map<string, Profile>>(new Map());
  const [events, setEvents] = useState<Map<string, EventRow>>(new Map());
  const [eventFilter, setEventFilter] = useState<string>("all");
  const [kindFilter, setKindFilter] = useState<"all" | "like" | "share">("all");
  const [search, setSearch] = useState("");
  const [loadingList, setLoadingList] = useState(true);


  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  const load = useCallback(async () => {
    setLoadingList(true);
    const [likesRes, sharesRes] = await Promise.all([
      supabase
        .from("event_likes")
        .select("id,event_id,user_id,created_at")
        .order("created_at", { ascending: false })
        .limit(2000),
      supabase
        .from("event_shares")
        .select("id,event_id,user_id,created_at")
        .order("created_at", { ascending: false })
        .limit(2000),
    ]);
    if (likesRes.error) {
      toast.error(likesRes.error.message);
      setLoadingList(false);
      return;
    }
    const list: LikeRow[] = [
      ...((likesRes.data ?? []) as Omit<LikeRow, "kind">[]).map((r) => ({ ...r, kind: "like" as const })),
      ...((sharesRes.data ?? []) as Omit<LikeRow, "kind">[]).map((r) => ({ ...r, kind: "share" as const })),
    ].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    setRows(list);

    const userIds = [...new Set(list.map((r) => r.user_id))];
    const eventIds = [...new Set(list.map((r) => r.event_id))];
    const [{ data: profs }, { data: evs }] = await Promise.all([
      userIds.length
        ? supabase.from("profiles").select("id,first_name,last_name,email").in("id", userIds)
        : Promise.resolve({ data: [] as Profile[] }),
      eventIds.length
        ? supabase.from("events").select("id,name,starts_at").in("id", eventIds)
        : Promise.resolve({ data: [] as EventRow[] }),
    ]);
    setProfiles(new Map(((profs ?? []) as Profile[]).map((p) => [p.id, p])));
    setEvents(new Map(((evs ?? []) as EventRow[]).map((e) => [e.id, e])));
    setLoadingList(false);
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const eventCounts = useMemo(() => {
    const m = new Map<string, { likes: number; shares: number }>();
    rows.forEach((r) => {
      const cur = m.get(r.event_id) ?? { likes: 0, shares: 0 };
      if (r.kind === "like") cur.likes += 1;
      else cur.shares += 1;
      m.set(r.event_id, cur);
    });
    return [...m.entries()].sort((a, b) => b[1].likes + b[1].shares - (a[1].likes + a[1].shares));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (kindFilter !== "all" && r.kind !== kindFilter) return false;
      if (eventFilter !== "all" && r.event_id !== eventFilter) return false;
      if (!q) return true;
      const p = profiles.get(r.user_id);
      const name = `${p?.first_name ?? ""} ${p?.last_name ?? ""} ${p?.email ?? ""}`.toLowerCase();
      return name.includes(q);
    });
  }, [rows, eventFilter, kindFilter, search, profiles]);

  if (loading || !user || !isAdmin) return null;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto w-full max-w-6xl px-4 py-8">
        <AdminTabs />
        <h1 className="mb-1 text-2xl font-bold">Beğeniler & Paylaşımlar</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Hangi üyenin hangi etkinliği beğendiği ve paylaştığı.
        </p>

        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {eventCounts.map(([id, c]) => (
            <button
              key={id}
              type="button"
              onClick={() => setEventFilter(eventFilter === id ? "all" : id)}
              className={`rounded-xl border p-4 text-left transition-colors ${
                eventFilter === id ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-muted"
              }`}
            >
              <div className="text-sm font-semibold">{events.get(id)?.name ?? "—"}</div>
              <div className="mt-1 flex items-center gap-4">
                <span className="flex items-center gap-1.5 text-destructive">
                  <Heart className="h-4 w-4 fill-current" />
                  <span className="text-lg font-bold">{c.likes}</span>
                </span>
                <span className="flex items-center gap-1.5 text-primary">
                  <Send className="h-4 w-4" />
                  <span className="text-lg font-bold">{c.shares}</span>
                </span>
              </div>
            </button>
          ))}
        </div>


        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-border p-0.5">
            {([
              ["all", "Tümü"],
              ["like", "Beğeni"],
              ["share", "Paylaşım"],
            ] as const).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setKindFilter(k)}
                className={`h-9 rounded-md px-3 text-sm ${
                  kindFilter === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Üye ara (isim veya e-posta)"
            className="h-10 w-full max-w-xs rounded-lg border border-border bg-background px-3 text-sm"
          />
          {eventFilter !== "all" && (
            <button
              type="button"
              onClick={() => setEventFilter("all")}
              className="h-10 rounded-lg border border-border px-3 text-sm"
            >
              Filtreyi temizle
            </button>
          )}
          <span className="text-sm text-muted-foreground">{filtered.length} kayıt</span>
        </div>

        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">İşlem</th>
                <th className="px-4 py-3 font-medium">Üye</th>
                <th className="px-4 py-3 font-medium">E-posta</th>
                <th className="px-4 py-3 font-medium">Etkinlik</th>
                <th className="px-4 py-3 font-medium">Tarih</th>
              </tr>
            </thead>
            <tbody>
              {loadingList ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Yükleniyor…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Kayıt yok</td></tr>
              ) : (
                filtered.map((r) => {
                  const p = profiles.get(r.user_id);
                  return (
                    <tr key={`${r.kind}-${r.id}`} className="border-b border-border/60 last:border-0">
                      <td className="px-4 py-3">
                        {r.kind === "like" ? (
                          <span className="inline-flex items-center gap-1.5 text-destructive">
                            <Heart className="h-4 w-4 fill-current" /> Beğeni
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-primary">
                            <Send className="h-4 w-4" /> Paylaşım
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {p ? `${p.first_name} ${p.last_name}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{p?.email ?? "—"}</td>
                      <td className="px-4 py-3">{events.get(r.event_id)?.name ?? "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(r.created_at).toLocaleString("tr-TR")}
                      </td>
                    </tr>
                  );
                })
              )}

            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
